import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { proper } from "lib/common/proper";

import {
  useRecoilState,
  useRecoilValueLoadable,
  useSetRecoilState,
} from "recoil";
import {
  asyncGetProjectList,
  projectListUpdate,
  createProject,
  deleteProject,
  updateProject,
  currentProject,
  projectFilterState,
} from "lib/store/project";
import {
  customToastState,
  errorToastState,
  modalState,
  resetState,
} from "lib/store/common";

import {
  NewIcon,
  ProjectCard,
  ProjectRightClickMenu,
} from "@atsent/components";

import { HeaderNFooter } from "components/template/HeaderNFooter";
import { CategorySelect } from "components/modals/CategorySelect";
import { CaratSort } from "components/atom/CaratSort";

import "./MainPage.scss";
import { RequestCategory } from "components/modals/RequestCategory";

const Card = ({
  title,
  category,
  analyzed_at,
  idx,
  sentiment,
  isNew = false,
}: {
  title: string;
  category: string;
  analyzed_at: string;
  idx: number;
  sentiment:
    | "positive-1"
    | "positive-2"
    | "positive-3"
    | "neutral"
    | "negative-1"
    | "negative-2"
    | "negative-3"
    | "default";
  isNew?: boolean;
}) => {
  const updateProjectList = useSetRecoilState(projectListUpdate);
  const setPid = useSetRecoilState(currentProject);
  const setErrorToast = useSetRecoilState(errorToastState);
  const reset = useSetRecoilState(resetState);

  const [click, setClick] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [text, setText] = useState<string | null>(null);
  const [showContextMenu, setShowContextMenu] = useState(false);

  const navigate = useNavigate();

  const handleModifyProject = async () => {
    if (!text) return;
    await updateProject(idx, { name: text });
    await updateProjectList(new Date());
    await setText(null);
  };

  const list = [
    {
      text: "프로젝트 보기",
      click: () => handleClickProject(),
    },
    // { text: '링크 복사', click: () => console.log('링크 복사 clicl') },
    // { text: '내보내기', click: () => console.log('내보내기 clicl') },
    {
      text: "이름 편집",
      click: () => {
        setClick(true);
        setShowContextMenu(false);
      },
    },
    {
      text: "삭제",
      click: async () => {
        try {
          await deleteProject(idx);
          await updateProjectList(new Date());
        } catch (e) {
          setErrorToast(e);
        }
      },
    },
  ];

  const handleClickProject = () => {
    setPid(idx);
    reset((_) => _);
    navigate(`/${idx}${!analyzed_at ? "/demo" : ""}`);
  };

  useEffect(() => {
    if (pos.x > 0 && pos.y > 0) setShowContextMenu(true);
  }, [pos]);
  return (
    <>
      <ProjectCard
        cx={`${showContextMenu ? "inputMode" : ""}`}
        sentiment={sentiment}
        title={title}
        category={proper(category)}
        isInput={click}
        isNew={isNew}
        handleBlur={() => {
          setClick(false);
        }}
        onClick={() => handleClickProject()}
        handleKeyUp={() => handleModifyProject()}
        handleChange={(e: any) => setText(e.target.value)}
        rightClick={(e: any) => {
          e.preventDefault();
          setPos({
            x: e.clientX,
            y: e.clientY,
          });
        }}
      />
      {showContextMenu && (
        <ProjectRightClickMenu
          pos={pos}
          list={list}
          onMouseLeave={() => setShowContextMenu(false)}
        />
      )}
    </>
  );
};

export const MainPage = () => {
  const [showModal, setShowModal] = useRecoilState(modalState);
  const projectList = useRecoilValueLoadable(asyncGetProjectList);
  const updateProjectList = useSetRecoilState(projectListUpdate);

  const setToast = useSetRecoilState(customToastState);

  const [text, setText] = useState<string | null>(null);
  const [newProject, setNewProject] = useState(false);
  const [orderName, setOrderName] = useState(0);

  const setFilter = useSetRecoilState(projectFilterState);

  const handleCreateProject = async ({
    key,
    value,
  }: {
    key: string;
    value: string;
  }) => {
    if (!text || !key || !value) {
      setNewProject(false);
      return;
    }
    await createProject({ name: text, category: key });
    await updateProjectList(new Date());
    await setNewProject(false);
    await setText(null);
    await setShowModal((modals) => ({ ...modals, categorySelect: false }));
  };

  const handleCheckTitle = () => {
    if (!text || text.length < 1) {
      if (projectList.contents.length > 0) {
        setNewProject(false);
      } else {
        setToast({
          text: "프로젝트 제목을 입력 해 주세요.",
          type: "error",
        });
      }
    } else {
      setShowModal((modals) => ({ ...modals, categorySelect: true }));
    }
  };

  return (
    <HeaderNFooter cx={"MainPage"}>
      <section>
        <div className="line">
          <span className={"bold"}>Pre-train된 새로운 감성 분석 모델.</span>
          <div className={"carat-wrapper"}>
            <CaratSort
              text={"이름순"}
              state={orderName}
              setState={async () => {
                await setOrderName((prev) => (prev === 2 ? 0 : prev + 1));
                await setFilter((filter) => ({
                  ...filter,
                  ordering: orderName === 1 ? "name" : "-name",
                }));
              }}
            />
          </div>
        </div>
        <span className={"project-count"}>
          Project {projectList.contents.length}
        </span>
        <div className="card-wrapper">
          {projectList.state === "hasValue" &&
            projectList.contents.map(
              ({
                name,
                category,
                analyzed_at,
                sentiment,
                id,
              }: {
                name: string;
                category: string;
                analyzed_at: string;
                sentiment:
                  | "positive-1"
                  | "positive-2"
                  | "positive-3"
                  | "neutral"
                  | "negative-1"
                  | "negative-2"
                  | "negative-3"
                  | "default";
                id: number;
              }) => (
                <Card
                  title={name}
                  idx={id}
                  category={category}
                  analyzed_at={analyzed_at}
                  sentiment={sentiment}
                  key={`${name}-${id}`}
                />
              )
            )}
          {(newProject || projectList.contents.length === 0) && (
            <ProjectCard
              isInput={true}
              category={""}
              title={""}
              handleKeyUp={() => handleCheckTitle()}
              handleChange={(e: any) => setText(e.target.value)}
              handleBlur={() => handleCheckTitle()}
              isNew
            />
          )}
          <NewIcon onClick={() => setNewProject(true)} />
        </div>
      </section>
      {showModal.categorySelect && (
        <CategorySelect
          onClick={async (value: { key: string; value: string }) => {
            await handleCreateProject(value);
          }}
          close={() => {
            setNewProject(false);
            setShowModal((modals) => ({ ...modals, categorySelect: false }));
          }}
        />
      )}
      {showModal.requestCategory && <RequestCategory />}
    </HeaderNFooter>
  );
};
